import type { PriceTagV2Price } from '@synoptic/ui-kit/price-tag-v2/price-tag-v2.js';
import type { StreamCardFragmentFragment } from '@/__generated__/graphql.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import {
  getMinPurchasableTargets,
  getMonthlyPrice,
  getStartingMonthlyPrice,
} from '@/utils/paid-data.ts';

type StreamCardPricing = {
  paidData: StreamCardFragmentFragment['paidData'];
  customTargetsData: Pick<
    NonNullable<StreamCardFragmentFragment['customTargetsData']>,
    'minTargets' | 'stepSize'
  > | null;
  packageOnly?: boolean | null;
};

const formatDollars = (cents: number): string => {
  const dollars = convertCentsToDollars(cents);
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
};

export const streamCardPriceV2 = (
  stream: StreamCardPricing,
): PriceTagV2Price => {
  if (stream.packageOnly) {
    return 'inPackage';
  }

  const monthly = getMonthlyPrice(stream.paidData);
  if (monthly == null) {
    return 'free';
  }

  if (stream.customTargetsData) {
    const starting = getStartingMonthlyPrice(
      stream.paidData,
      stream.customTargetsData,
    );
    if (starting == null) {
      return 'free';
    }

    return {
      per: 'targets',
      amount: formatDollars(starting),
      targets: getMinPurchasableTargets(stream.customTargetsData),
    };
  }

  return { per: 'month', amount: formatDollars(monthly) };
};
